import { map } from 'rxjs/operators/map';
import ajax from '../../utils/ajax';
import { TagSerializer } from '../../models';
import { getUrlByQueryParms } from '../../utils/transform';

class TagService {
  conductorUrl = process.env.REACT_APP_CONDUCTOR_ASSETS_URL || '';
  clientEdgeBase = process.env.REACT_APP_CLIENT_EDGE_BASE || '';
  configurationPathGroup = '/networkAsset/airfinder/tags';
  configurationPath = '/networkAsset/airfinder/tag';

  /**
    Create a Tag
    @param {Object} Tag
    @param {Object} store Redux store
    @returns {Observable} Observable that emits the normalized response
  */
  create = (tag, store) => {
    return ajax(
      {
        url: `${this.conductorUrl}${this.configurationPathGroup}`,
        body: TagSerializer.serialize(tag),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        responseType: 'json'
      },
      store
    ).pipe(
      map(data => {
        let serializedTag = TagSerializer.normalize(data.response);
        serializedTag.site = tag.site;
        serializedTag.category = tag.category;
        serializedTag.group = tag.group;
        if (!serializedTag.categoryName && tag.category) {
          serializedTag.categoryName = tag.category.name;
        }
        if (!serializedTag.groupName && tag.group) {
          serializedTag.groupName = tag.group.name;
        }
        return serializedTag;
      })
    );
  };

  /**
    Delete a Tag
    @param {Object} Tag
    @param {Object} store Redux store
    @returns {Observable} Observable that emits the response
  */
  delete = (tag, store) => {
    return ajax(
      {
        url: `${this.conductorUrl}${this.configurationPath}`,
        body: TagSerializer.serializeDelete(tag),
        headers: { 'Content-Type': 'application/json' },
        method: 'DELETE',
        responseType: 'json'
      },
      store
    );
  };

  /**
    Edit a Tag
    @param {Object} Tag
    @param {Object} store Redux store
    @returns {Observable} Observable that emits the normalized response
  */
  edit = (tag, store) => {
    return ajax(
      {
        url: `${this.conductorUrl}${this.configurationPath}/${tag.id}`,
        body: TagSerializer.serializeEdit(tag),
        headers: { 'Content-Type': 'application/json' },
        method: 'PUT',
        responseType: 'json'
      },
      store
    ).pipe(map(data => TagSerializer.normalize(data.response)));
  };

  /**
    Get Tags by site id
    @param {String} siteId
    @param {Object} store Redux store
    @returns {Observable} Observable that emits the normalized tags
  */
  getAll = (siteId, store) => {
    let url = `${this.conductorUrl}${this.configurationPathGroup}?siteId=${siteId}`;
    return ajax(
      {
        url: url,
        method: 'GET',
        responseType: 'json'
      },
      store
    ).pipe(
      map(data =>
        data.response.reduce(
          (map, { areaId, tags }) => ({
            ...map,
            [areaId]: {
              areaId,
              tags: tags.map(TagSerializer.normalize)
            }
          }),
          {}
        )
      )
    );
  };

    /**
  Get Filters by site id
  @param {String} siteId
  @param {Object} store Redux store
  @returns {Observable} Observable that emits the normalized tags
  */
 getFiltersBySiteId = (siteId, store) => {
  let url = `${this.conductorUrl}${this.configurationPathGroup}/uniqueprops?siteId=${siteId}&props=field1,field2`;
  return ajax(
    {
      url: url,
      method: 'GET',
      responseType: 'json'
    },
    store
  ).pipe(map(data => {
    return data.response;
  }
  ));
};

  /**
  Get Tags by site id
  @param {String} siteId
  @param {Object} store Redux store
  @returns {Observable} Observable that emits the normalized tags
  */
  getBySiteId = (siteId, page, size, searchText, sorting, filters, areaId, store) => {
    let url = `${this.conductorUrl}${this.configurationPathGroup}?siteId=${siteId}&groupBy=none`;
    if (page && size) {
      url = `${url}&page=${page}&size=${size}`;
    }
    if(searchText){
      url=`${url}&search=${encodeURIComponent(searchText)}`;
    }
    if(sorting) {
      const { column: { sortField }, descendingOrder } = sorting;
      url = `${url}&sortBy=${sortField}&sort=${descendingOrder?'dsc':'asc'}`
		}
    if (areaId) {
      url = `${url}&areaId=eq:${areaId}`
    }
    if (filters && filters.length) {
      filters.forEach(filter => {
        if(filter.key === 'batteryStatus'){
          url = `${url}&${filter.key}=eq:${filter.value === 'Good' ? 1 : 0}`;
          return;
				}
				if(filter.key === 'locationName'){
          url = `${url}&${filter.key}=cs:${filter.value}`;
          return;
        }
        url = `${url}&${filter.key}=eq:${filter.value}`;
      });
    }
    return ajax(
      {
        url: url,
        method: 'GET',
        responseType: 'json'
      },
      store
    ).pipe(map(data => {
      const pagedCount = data.xhr.getResponseHeader('TOTAL_FILTERED_RECORDS') || 0;
      const totalCount = data.xhr.getResponseHeader('TOTAL_SITE_RECORDS') || 0;
      let res = {
        total: pagedCount,
        totalInSite: totalCount,
        data: data.response.map(TagSerializer.normalize)
      }
      return res;
    }
    ));
  };

  /**
    Get the asset group by site
    @param {String} siteId
    @param {Object} store Redux store
    @returns {Observable} Observable that emits the asset group id
  */
  getAssetGroup = (siteId, store) => {
    return ajax(
      {
        url:
          `${this.conductorUrl}${this.configurationPathGroup}/assetGroup` +
          `?siteId=${siteId}`,
        method: 'GET',
        responseType: 'json'
      },
      store
    ).pipe(map(data => data.response && data.response.id));
  };

  /**
    Get websocket for tag stream
    @param {String} assetGroup Id for the asset group
    @param {Object} store Redux store
    @returns {Observable} Observable that emits the normalized response
  */
  getWebSocketUrl = (assetGroup, store) => {
    return ajax(
      {
        url:
          `${this.clientEdgeBase}/data/airfinderLocation/assetGroup/` +
          `${assetGroup}/subscriptions`,
        body: {
          subscriptionProperties: { filterProperties: [] },
          channelRequest: { type: 'Websocket' }
        },
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        responseType: 'json'
      },
      store
    ).pipe(map(data => data.response.websocketUrl.href));
  };

  /**
    Get super tag history
    @param {String} id Tag id
    @param {Date} startDate Start date to get history
    @param {Date} endDate End date to get history
    @param {Object} store Redux store
    @returns {Observable} Observable that emits the normalized response
  */
  getHistory = (id, startDate, endDate, store) => {
    let url = `${this.clientEdgeBase}/data/airfinderLocation/node/${id}/events/${endDate.toISOString()}/${startDate.toISOString()}`;
    return ajax(
      {
        url: url,
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
        responseType: 'json'
      },
      store
    ).pipe(
      map(data => {
        let d =  data.response.results.map(TagSerializer.serializeHistory);
        return d.reverse(); //comes back end-> start from api
      })
    );
  };

  /**
    Bulk upload tags
    @param {File} file CSV file to upload
    @param {String} siteId Site to add the tags
    @param {Object} store Redux store
    @returns {Observable} Observable that emits the normalized response
  */
  bulkUpload = (file, siteId, store) => {
    let data = new FormData();
    data.append('file', file, file.name);
    return ajax(
      {
        url: `${this.conductorUrl}${
          this.configurationPathGroup
        }/bulk?siteId=${siteId}`,
        body: data,
        method: 'POST',
        responseType: 'json'
      },
      store
    ).pipe(map(data => TagSerializer.normalizeBulkUpload(data.response)));
  };

  /**
    Download CSV tag history
    @param {List} tags List of tags
    @param {Date} startDate Start date to get history
    @param {Date} endDate End date to get history
    @param {Object} store Redux store
    @returns {Observable} Observable that emits the normalized response
  */
  downloadCSVHistory = (tags, startDate, endDate, store) => {
    return ajax(
      {
        url: `${this.clientEdgeBase}/data/airfinderLocation/node/${
          tags[0]
        }/events.csv/${endDate.toISOString()}/${startDate.toISOString()}`,
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
        responseType: 'text'
      },
      store
    ).pipe(map(data => data.response));
  };
  exportTagList = (siteId, queryParams, store)=>{
    let url = `${this.conductorUrl}${this.configurationPathGroup}?siteId=${siteId}`;
    
    if(queryParams){
      url = `${url}${getUrlByQueryParms(queryParams)}`;
    }
    return ajax(
      {
        url,
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
        responseType: 'text'
      },
      store
    ).pipe(map(data => data.response));
  };
}

export default new TagService();



// WEBPACK FOOTER //
// ./src/services/tag/index.js